import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { selfRegisterApi } from '../api/selfRegister.api';
import OtpChallengePanel from '../components/OtpChallengePanel';
import { DEFAULT_TENANT_SLUG } from '@/config/singleTenant';
import { IITH, SERIF, SANS } from '@/pages/landing/iithBrand';

export default function SelfRegisterPage() {
  const navigate = useNavigate();
  const slug = DEFAULT_TENANT_SLUG;
  const [affiliations, setAffiliations] = useState([]);
  const [clinic, setClinic] = useState(IITH.hospital);
  const [requirePhone, setRequirePhone] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', role_key: '', affiliation_note: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await selfRegisterApi.options(slug);
        setAffiliations(data.affiliations || []);
        setClinic(data.clinic || IITH.hospital);
        setRequirePhone(Boolean(data.require_phone));
        if (data.affiliations?.[0]) {
          setForm((f) => ({ ...f, role_key: data.affiliations[0].role_key }));
        }
      } catch (_) {
        toast.error('Could not load registration options');
      }
      setLoading(false);
    })();
  }, [slug]);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.role_key) {
      toast.error('Name, email, password and affiliation are required');
      return;
    }
    if (requirePhone && (form.phone || '').replace(/\D/g, '').length < 10) {
      toast.error('A valid phone number is required');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      const res = await selfRegisterApi.requestOtp(slug, form);
      setChallenge(res);
      toast.success('OTP ready — enter the code to finish');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start verification');
    }
    setSaving(false);
  };

  const confirmOtp = async (otp) => {
    setSaving(true);
    try {
      const res = await selfRegisterApi.confirm(slug, {
        challenge_id: challenge.challenge_id,
        otp,
      });
      toast.success(res.message || 'Account created');
      navigate('/login', { replace: true, state: { registeredEmail: form.email } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'OTP verification failed');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen" style={{ background: IITH.mist }} data-testid="self-register-page">
      <div
        className="px-5 py-8 sm:py-12"
        style={{ background: `linear-gradient(120deg, ${IITH.navy} 0%, ${IITH.navyMid} 100%)` }}
      >
        <div className="max-w-lg mx-auto text-center text-white">
          <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: IITH.gold }}>
            {clinic}
          </p>
          <h1 className="text-3xl font-semibold mt-2" style={{ fontFamily: SERIF }}>
            {challenge ? 'Verify your contact' : 'Create your account'}
          </h1>
          <p className="mt-2 text-sm" style={{ color: IITH.mute, fontFamily: SANS }}>
            {challenge
              ? 'Step 2 of 2 — enter the OTP to confirm email / phone.'
              : 'Step 1 of 2 — details, then OTP verification.'}
          </p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-6 pb-16">
        <div className="bg-white border rounded-2xl shadow-lg p-6 sm:p-8 space-y-4">
          {loading ? (
            <div className="py-10 text-center text-zinc-500">
              <Loader2 className="h-5 w-5 animate-spin inline" />
            </div>
          ) : challenge ? (
            <OtpChallengePanel
              challenge={challenge}
              verifying={saving}
              onVerify={confirmOtp}
              onBack={() => setChallenge(null)}
            />
          ) : (
            <form onSubmit={requestOtp} className="space-y-4">
              <div className="space-y-1.5">
                <Label>I am a *</Label>
                <select
                  className="w-full h-11 border rounded-md px-3 text-sm"
                  value={form.role_key}
                  onChange={(e) => setForm({ ...form, role_key: e.target.value })}
                  data-testid="register-affiliation"
                  required
                >
                  {affiliations.map((a) => (
                    <option key={a.role_key} value={a.role_key}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Full name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="register-name" required />
              </div>
              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="register-email" required />
              </div>
              <div className="space-y-1.5">
                <Label>Phone {requirePhone ? '*' : ''}</Label>
                <Input value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Mobile number for OTP"
                  data-testid="register-phone" required={requirePhone} />
              </div>
              <div className="space-y-1.5">
                <Label>Password *</Label>
                <Input type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="register-password" required minLength={8} />
              </div>
              <div className="space-y-1.5">
                <Label>Department / project / note</Label>
                <Input value={form.affiliation_note}
                  onChange={(e) => setForm({ ...form, affiliation_note: e.target.value })}
                  data-testid="register-note" />
              </div>
              <Button type="submit" disabled={saving} className="w-full text-white h-11"
                style={{ background: IITH.saffron }} data-testid="register-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Send OTP
              </Button>
            </form>
          )}
          <p className="text-center text-sm text-zinc-500 pt-2">
            Already registered?{' '}
            <Link to="/login" className="font-medium underline underline-offset-2" style={{ color: IITH.navy }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
